/**
 * Connection Service — OAuth2 connection management for O365.
 *
 * All calls use Cognito JWT via authService.getIdToken().
 * Uses REACT_APP_CONNECTION_API_URL env var for API base.
 *
 * See: Architecture Plan — Area 2, Step 8
 */

import authService from './authService';

const API_BASE = process.env.REACT_APP_CONNECTION_API_URL || 'https://api-platform.niwt.com';

// ─── Types ─────────────────────────────────────────────────────────

export type CapabilityName = 'directory' | 'mail' | 'calendar' | 'sharepoint' | 'teams';

export type CapabilityStatus = 'not_configured' | 'pending' | 'testing' | 'active' | 'error' | 'not_tested';

export interface CapabilityInfo {
  status: CapabilityStatus;
  testedAt?: string;
  error?: string;
}

export type CapabilitiesMap = Partial<Record<CapabilityName, CapabilityInfo>>;

export interface Connection {
  provider: 'o365' | 'google';
  authMode: 'delegated_service_account' | 'app_only';
  status: 'pending' | 'testing' | 'active' | 'error' | 'revoked';
  providerTenantId?: string;
  serviceAccountUpn?: string;
  connectedAt?: string;
  connectedBy?: string;
  lastTestedAt?: string;
  lastError?: string;
  permissionsGranted?: string[];
  enabledCapabilities?: CapabilityName[];
  capabilities?: CapabilitiesMap;
}

export interface SubsystemResult {
  status: 'ok' | 'fail' | 'not_tested';
  errorCode?: string;
  remediationHint?: string;
  userCount?: number;
}

export interface AccessTestResult {
  directory: SubsystemResult;
  mail: SubsystemResult;
  calendar: SubsystemResult;
  sharepoint: SubsystemResult;
  overall: 'pass' | 'fail';
}

export interface ConnectionResult {
  status: 'active' | 'error';
  testResults: AccessTestResult;
  providerTenantId?: string;
  serviceAccountUpn?: string;
}

export interface UnlockResult {
  action: 'consent_required' | 'tested';
  authUrl?: string;
  state?: string;
  capability?: CapabilityName;
  result?: SubsystemResult;
  testResults?: AccessTestResult;
}

export interface DirectoryUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle: string | null;
  department: string | null;
  licensed: boolean;
  mailEnabled: boolean;
  accountEnabled: boolean;
}

// ─── HTTP helper ───────────────────────────────────────────────────

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await authService.getIdToken();

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.error || `Request failed with status ${response.status}`;
    const error = new Error(message) as any;
    error.errorCode = errorBody.error_code;
    error.status = response.status;
    throw error;
  }

  return response.json();
}

// ─── Plan A (Delegated) ────────────────────────────────────────────

/**
 * Start the delegated OAuth flow. Returns the Microsoft auth URL.
 * Store the state in sessionStorage for CSRF validation on callback.
 */
export async function startDelegatedConnection(
  capabilities?: CapabilityName[]
): Promise<{ authUrl: string; state: string }> {
  const result = await apiRequest<{ authUrl: string; state: string }>(
    '/api/connections/o365/delegated/start',
    {
      method: 'POST',
      body: JSON.stringify(capabilities ? { capabilities } : {}),
    }
  );

  // Store state in sessionStorage for CSRF validation
  sessionStorage.setItem('niwt_oauth_state', result.state);

  return result;
}

/**
 * Complete the delegated OAuth flow after Microsoft callback.
 * Validates state nonce from sessionStorage.
 */
export async function completeDelegatedConnection(
  code: string,
  state: string
): Promise<ConnectionResult> {
  // Validate state against sessionStorage
  const savedState = sessionStorage.getItem('niwt_oauth_state');
  if (!savedState || savedState !== state) {
    throw new Error('OAuth state mismatch — possible CSRF attack. Please restart the connection.');
  }

  const result = await apiRequest<ConnectionResult>(
    '/api/connections/o365/delegated/callback',
    {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    }
  );

  // Clean up state
  sessionStorage.removeItem('niwt_oauth_state');

  return result;
}

// ─── Plan B (App-Only) ─────────────────────────────────────────────

/**
 * Register an app-only connection.
 *
 * Stage 1 (no testMailboxUserId): registers a pending connection so directory-sync works.
 * Stage 2 (with testMailboxUserId): runs Phase 1 access test and activates.
 */
export async function registerAppOnlyConnection(
  azureTenantId: string,
  testMailboxUserId?: string
): Promise<ConnectionResult> {
  const payload: Record<string, string> = { azureTenantId };
  if (testMailboxUserId) payload.testMailboxUserId = testMailboxUserId;

  return apiRequest<ConnectionResult>(
    '/api/connections/o365/app-only',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

// ─── Shared ────────────────────────────────────────────────────────

/**
 * Re-run the access test on an existing connection.
 * Optionally test a single capability.
 */
export async function testConnection(capability?: CapabilityName): Promise<ConnectionResult> {
  const qs = capability ? `?capability=${capability}` : '';
  return apiRequest<ConnectionResult>(
    `/api/connections/o365/test${qs}`,
    { method: 'POST' }
  );
}

/**
 * Unlock an additional capability beyond Phase 1.
 * For Plan A: returns consent URL. For Plan B: runs test immediately.
 */
export async function unlockCapability(capability: CapabilityName): Promise<UnlockResult> {
  return apiRequest<UnlockResult>(
    '/api/connections/o365/unlock',
    {
      method: 'POST',
      body: JSON.stringify({ capability }),
    }
  );
}

/**
 * Test a single capability.
 */
export async function testCapability(capability: CapabilityName): Promise<ConnectionResult> {
  return testConnection(capability);
}

/**
 * List all connections for the current tenant.
 */
export async function getConnections(): Promise<Connection[]> {
  const result = await apiRequest<{ connections: Connection[] }>('/api/connections');
  return result.connections;
}

/**
 * Disconnect O365. Removes secrets and revokes connection.
 */
export async function disconnectO365(): Promise<void> {
  await apiRequest('/api/connections/o365', { method: 'DELETE' });
}

/**
 * Get directory users from the connected O365 tenant.
 */
export async function getDirectoryUsers(
  options: { pageSize?: number; nextLink?: string } = {}
): Promise<{ users: DirectoryUser[]; nextLink: string | null }> {
  const params = new URLSearchParams();
  if (options.pageSize) params.set('pageSize', String(options.pageSize));
  if (options.nextLink) params.set('nextLink', options.nextLink);

  const qs = params.toString();
  return apiRequest<{ users: DirectoryUser[]; nextLink: string | null }>(
    `/api/directory/users${qs ? `?${qs}` : ''}`
  );
}

const connectionService = {
  startDelegatedConnection,
  completeDelegatedConnection,
  registerAppOnlyConnection,
  testConnection,
  testCapability,
  unlockCapability,
  getConnections,
  disconnectO365,
  getDirectoryUsers,
};

export default connectionService;
